// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';

// project imports
import LogoSection from '../LogoSection';
import SearchSection from './SearchSection';
import MobileSection from './MobileSection';
import ProfileSection from './ProfileSection';
import { useNavTheme } from 'contexts/NavThemeContext';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';

// assets
import { IconMenu2, IconSun, IconMoon } from '@tabler/icons-react';

// ==============================|| MAIN NAVBAR / HEADER ||============================== //

export default function Header() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleMode } = useNavTheme();
  const isLight = mode === 'light';

  const {
    state: { menuOrientation }
  } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  return (
    <>
      {/* logo & toggler button */}
      <Box sx={{ width: downMD ? 'auto' : 228, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
          <LogoSection />
        </Box>
        {!isHorizontal && (
          <ButtonBase
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.7)',
              background: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.04)',
              border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                background: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)',
                border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`,
              },
            }}
            onClick={() => handlerDrawerOpen(!drawerOpen)}
          >
            <IconMenu2 stroke={1.5} size="20px" />
          </ButtonBase>
        )}
      </Box>

      {/* header search / organization */}
      <SearchSection />
      <Box sx={{ flexGrow: 1 }} />

      {/* Theme Toggle Button - inverted colors for visibility */}
      <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
        <ButtonBase
          onClick={toggleMode}
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
            // Inverted colors: dark button in light mode, light button in dark mode
            color: isLight ? '#ffffff' : '#1e293b',
            background: isLight ? '#1e293b' : '#ffffff',
            border: 'none',
            boxShadow: isLight
              ? '0 2px 8px rgba(30, 41, 59, 0.3)'
              : '0 2px 8px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: isLight ? '#334155' : '#f1f5f9',
              transform: 'scale(1.05)',
            },
          }}
        >
          {mode === 'dark' ? (
            <IconSun size={18} stroke={2} />
          ) : (
            <IconMoon size={18} stroke={2} />
          )}
        </ButtonBase>
      </Tooltip>

      {/* profile */}
      <ProfileSection />

      {/* mobile header */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <MobileSection />
      </Box>
    </>
  );
}
